/**
 * Plain-language progress for private actions: what each relayed transaction
 * does and how long it should take. Every step is one THORChain transaction,
 * so its expected time is one block plus proof generation.
 */
import type { V2Call, V2Coin } from "./action";
import { base64Decode } from "./encoding";

export const DEFAULT_BLOCK_TIME_MS = 6_000;
/** Initial proof time estimate; the session replaces it with measurements. */
export const DEFAULT_PROVE_MS = 3_000;
/** The relayer polls for inclusion every 3 s after the block. */
const RELAY_OVERHEAD_MS = 1_500;

/** Ticker of a THORChain bank denom: "btc-btc" → BTC, "x/ruji" → RUJI. */
export const denomSymbol = (denom: string): string => {
  const lower = denom.toLowerCase();
  if (lower === "rune") return "RUNE";
  if (lower.startsWith("x/staking-"))
    return `s${denomSymbol(lower.slice("x/staking-".length))}`;
  if (lower.startsWith("x/"))
    return (lower.slice(2).split("/").pop() || lower).toUpperCase();
  const parts = lower.split("-");
  return (parts.length >= 2 ? parts[1] : parts[0]).toUpperCase();
};

/** Ticker of a THORChain memo asset: "LTC-LTC", "BTC.BTC", "ETH-USDC-0X…". */
export const memoAssetSymbol = (asset: string): string => {
  const parts = asset.toUpperCase().split(/[.~-]/);
  return parts.length >= 2 ? parts[1] : parts[0];
};

/** THORChain bank amounts use 8 decimals. Truncates, never rounds up. */
export const formatNativeAmount = (
  amount: string | bigint,
  decimals = 8
): string => {
  const value = BigInt(amount);
  const base = 10n ** BigInt(decimals);
  const whole = value / base;
  const digits = whole >= 1_000n ? 2 : whole >= 1n ? 4 : decimals;
  const fraction = (value % base)
    .toString()
    .padStart(decimals, "0")
    .slice(0, digits)
    .replace(/0+$/, "");
  const grouped = whole.toLocaleString("en-US");
  return fraction ? `${grouped}.${fraction}` : grouped;
};

export const formatCoins = (coins: readonly Readonly<V2Coin>[]): string =>
  coins
    .map(
      (coin) => `${formatNativeAmount(coin.amount)} ${denomSymbol(coin.denom)}`
    )
    .join(" + ");

const SWAP_MEMO = /^(=|s|swap)[:<>]/i;
const ADD_MEMO = /^(\+|a|add):/i;
const WITHDRAW_MEMO = /^(-|wd|withdraw):/i;

const wasmMessage = (msg: string): Record<string, unknown> | undefined => {
  try {
    const parsed = JSON.parse(new TextDecoder().decode(base64Decode(msg)));
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
};

const symbolsOf = (coins: readonly Readonly<V2Coin>[]): string =>
  [...new Set(coins.map((coin) => denomSymbol(coin.denom)))].join(" + ");

/**
 * What one product call does, e.g. "Swapping BTC → LTC". Never the exact
 * amount: the toast is visible on screen and the amount is in the review.
 */
export const describeCall = (call: V2Call): string => {
  if ("thorchain_deposit" in call) {
    const { denom, memo } = call.thorchain_deposit;
    const from = denomSymbol(denom);
    if (SWAP_MEMO.test(memo)) {
      const target = memo.split(":")[1];
      return target
        ? `Swapping ${from} → ${memoAssetSymbol(target)}`
        : `Swapping ${from}`;
    }
    if (ADD_MEMO.test(memo)) return `Adding ${from} to a pool`;
    if (WITHDRAW_MEMO.test(memo)) return "Withdrawing from a pool";
    return `Depositing ${from}`;
  }
  if ("bank_send" in call) return `Sending ${symbolsOf(call.bank_send.amount)}`;
  const funds = call.wasm_execute.funds;
  const spend = funds.length ? ` ${symbolsOf(funds)}` : "";
  const msg = wasmMessage(call.wasm_execute.msg);
  const key = msg ? Object.keys(msg)[0] : undefined;
  const body = key ? msg?.[key] : undefined;
  const inner =
    body && typeof body === "object" && !Array.isArray(body)
      ? Object.keys(body)[0]
      : undefined;
  switch (key) {
    case "swap":
    case "market_swap":
      return `Swapping${spend}`;
    case "liquid":
    case "account":
      if (inner === "bond") return `Staking${spend}`;
      if (inner === "unbond") return `Unstaking${spend}`;
      if (inner === "claim") return "Claiming staking rewards";
      return "Updating your stake";
    case "order":
      return "Placing an order";
    case "deposit":
      return `Depositing${spend}`;
    case "withdraw":
      return "Withdrawing";
    case "claim":
      return "Claiming";
    case "range":
      return "Updating a liquidity range";
    case "instantiate":
      return "Creating a strategy";
    case "borrow":
      return "Borrowing";
    case "repay":
      return `Repaying${spend}`;
    default:
      return "Confirming your transaction";
  }
};

export const describeCalls = (calls: readonly V2Call[]): string =>
  calls.length === 0
    ? "Confirming your transaction"
    : calls.length === 1
      ? describeCall(calls[0])
      : `${describeCall(calls[0])} + ${calls.length - 1} more`;

/**
 * Reserve merges needed before one note covers `required`. The session merges
 * the two largest notes until one suffices, so this counts how many further
 * notes join the largest one.
 */
export const mergesNeeded = (
  amounts: readonly bigint[],
  required: bigint
): number => {
  const sorted = [...amounts].sort((left, right) =>
    left < right ? 1 : left > right ? -1 : 0
  );
  let total = sorted[0] ?? 0n;
  let merges = 0;
  for (let index = 1; total < required && index < sorted.length; index += 1) {
    total += sorted[index];
    merges += 1;
  }
  return merges;
};

export type BlockStamp = { height: number; time: string };

/** Average block time between two headers; undefined when implausible. */
export const averageBlockTimeMs = (
  latest: BlockStamp,
  earlier: BlockStamp
): number | undefined => {
  const blocks = latest.height - earlier.height;
  const elapsed = Date.parse(latest.time) - Date.parse(earlier.time);
  if (!(blocks > 0) || !Number.isFinite(elapsed) || elapsed <= 0)
    return undefined;
  const average = elapsed / blocks;
  return average >= 1_000 && average <= 30_000
    ? Math.round(average)
    : undefined;
};

export type OperationStepKey =
  | "prepare"
  | "combine"
  | "fund"
  | "submit"
  | "update";

/** Initial estimate for the checks before the first transaction. */
export const DEFAULT_PREPARE_MS = 2_500;

/**
 * Expected duration of a step. A relayed transaction lands in the next block
 * and the relayer reports it on its next poll; a proof built inside the step
 * adds the measured proving time.
 */
export const expectedStepMs = (
  key: OperationStepKey,
  blockTimeMs: number,
  proveMs: number,
  options: { proofReady?: boolean; prepareMs?: number } = {}
): number => {
  const transaction = blockTimeMs + RELAY_OVERHEAD_MS;
  switch (key) {
    case "prepare":
      return options.prepareMs ?? DEFAULT_PREPARE_MS;
    case "combine":
    case "fund":
      return proveMs + transaction;
    case "submit":
      return (options.proofReady ? 0 : proveMs) + transaction;
    case "update":
      return 2_000;
  }
};

/** A paused stretch of the Undo countdown: [from, to], open while it lasts. */
export type ProgressPause = readonly [number, number | undefined];

/** Time from `from` to `to` outside the paused stretches. */
export const activeMs = (
  from: number,
  to: number,
  pauses: readonly ProgressPause[] = []
): number => {
  if (!(to > from)) return 0;
  const paused = pauses.reduce((sum, [start, end]) => {
    const overlap = Math.min(end ?? to, to) - Math.max(start, from);
    return overlap > 0 ? sum + overlap : sum;
  }, 0);
  return Math.max(0, to - from - paused);
};

export type TimedStep = {
  state: "done" | "active" | "todo";
  startedAt?: number;
  expectedMs: number;
};

export type ProgressState = {
  /** When the action began, including a wait for a previous transfer. */
  startedAt: number;
  /** When the current step plan was first shown. */
  planAt?: number;
  /** Share of the bar already shown when that plan began. */
  progressBase?: number;
  /** After a re-plan: the time left shown before it, never shown higher. */
  remainingCap?: number;
  steps?: readonly TimedStep[];
  pauses?: readonly ProgressPause[];
  finishedAt?: number;
};

export type OperationProgress = {
  /** 0..1, and 1 only once the action is done. */
  share: number;
  /** Estimated time left over the unfinished steps. */
  remainingMs: number;
  /** Whether the time left is worth showing (never while a step runs late). */
  showTime: boolean;
  /** Only the last step is left and it is late or nearly done. */
  finishing: boolean;
};

/** Before a step plan exists, the bar creeps towards this share. */
const PLANNING_SHARE = 0.15;
const PLANNING_PACE_MS = 4_000;
/** A step counts as late once it runs this far past its estimate. */
const lateAfter = (expectedMs: number) => Math.max(1_000, expectedMs * 0.15);

/**
 * One model for the bar and the time left of a private action. Time only runs
 * while the Undo countdown is not paused. The bar moves at the pace that ends
 * the estimate on time and keeps creeping while a step runs late, so it never
 * stands still, never goes back and reaches 100% only when the action is done.
 * The time left never grows: it is hidden while a step runs late, and after a
 * re-plan until it is no higher than before.
 */
export const operationProgress = (
  state: ProgressState,
  now: number
): OperationProgress => {
  if (state.finishedAt !== undefined)
    return { share: 1, remainingMs: 0, showTime: false, finishing: true };
  const pauses = state.pauses ?? [];
  const steps = state.steps ?? [];
  if (!steps.length || state.planAt === undefined) {
    const waited = activeMs(state.startedAt, now, pauses);
    return {
      share: PLANNING_SHARE * (1 - Math.exp(-waited / PLANNING_PACE_MS)),
      remainingMs: 0,
      showTime: false,
      finishing: false,
    };
  }
  let remaining = 0;
  let late = false;
  let total = 0;
  for (const step of steps) {
    total += step.expectedMs;
    if (step.state === "todo") remaining += step.expectedMs;
    if (step.state !== "active") continue;
    const spent = activeMs(step.startedAt ?? now, now, pauses);
    remaining += Math.max(step.expectedMs - spent, 0);
    if (spent > step.expectedMs + lateAfter(step.expectedMs)) late = true;
  }
  const headroom = Math.max(total * 0.12, 1_500);
  const elapsed = activeMs(state.planAt, now, pauses);
  const base = Math.min(Math.max(state.progressBase ?? 0, 0), 0.99);
  const lastStep = !steps.some((step) => step.state === "todo");
  return {
    share: base + (1 - base) * (elapsed / (elapsed + remaining + headroom)),
    remainingMs: remaining,
    showTime:
      !late &&
      remaining >= 2_000 &&
      (state.remainingCap === undefined ||
        remaining <= state.remainingCap + 1_000),
    finishing: lastStep && (late || remaining < 2_000),
  };
};

/** Exponential moving average for measured proof (and preparation) times. */
export const nextProveEstimate = (
  previous: number,
  measured: number
): number =>
  measured > 0 && measured < 120_000
    ? Math.round(previous * 0.6 + measured * 0.4)
    : previous;
